<script setup lang="ts">
/**
 *
 * @name RequestSignModal
 * @author @Jorel254
 * @version 1.0.0
 *
 * @description COMPONENTE MODAL QUE PERMITE SOLICITAR LA FIRMA DE CERTIFICADOS
 *
 * responsabilidades:
 * R1: PERMITE VISUALIZAR LA INFORMACIÓN DEL PAQUETE DE CERTIFICADOS
 * R2: PERMITE MOSTRAR LOS RESPONSABLES QUE DEBEN FIRMAR
 * R3: PERMITE CAPTURAR LA FIRMA DEL RESPONSABLE
 * R4: PERMITE VALIDAR EL CERTIFICADO DEL RESPONSABLE
 * R5: PERMITE ENVIAR LAS FIRMAS AL SERVIDOR
 * R6: PERMITE MANEJAR EL MODO DE PRUEBA
 *
 * @changelog 1.0.0 - 2025-06-17 - @Jorel254 - Versión inicial del componente
 */

import { computed, ref } from 'vue'
import { ICertificatePackage } from '@shared/types/package'
import { certificateService } from '@/services/certificateService'
import FirmaComponent from '@/components/FirmaComponent.vue'
import { useStore } from 'vuex'
import { database } from '@/firebase/config'
import { ref as dbRef, query, orderByChild, equalTo, get } from 'firebase/database'
import { IExtendedCertificate } from '@shared/types/extensions'
import BaseModal from '../BaseModal.vue'

/**
 * @description Props del componente
 * @property {boolean} modelValue - Controla la visibilidad del modal
 * @property {ICertificatePackage | null} package - Paquete de certificados a firmar
 * @property {boolean} isTestMode - Indica si el componente está en modo prueba
 * @property {any} selectedInstitution - Institución seleccionada
 * @property {any} currentInstitution - Institución actual
 * @property {string[]} cadenasAFirmar - Lista de cadenas a firmar
 * @property {string} deviceId - ID del dispositivo
 */
const props = defineProps<{
	modelValue: boolean
	package: ICertificatePackage | null
	isTestMode: boolean
	selectedInstitution: any
	currentInstitution: any
	cadenasAFirmar: string[]
	deviceId: string
}>()
const showErrorModal = ref(false)
const errorMessage = ref<string>('')
/**
 * @description Store de Vuex
 */
const store = useStore()

/**
 * @description Referencia a la base de datos de Firebase
 */
const db = database

/**
 * @description Computed property que obtiene la institución actual
 * @type {ComputedRef<any>}
 */
const currentInstitution = computed(() => store.getters['global/currentInstitution'])

/**
 * @description Computed property que obtiene el modo de prueba
 * @type {ComputedRef<boolean>}
 */
const testMode = computed(() => store.getters.isTestMode)

/**
 * @description Función que obtiene la ruta de la institución en Firebase
 * @param {string} path - Ruta adicional
 * @returns {string} Ruta completa
 */
const getInstitutionPath = (path: string) => `institutions/${currentInstitution.value}${testMode.value ? '/testMode' : ''}${path}`

/**
 * @description Computed property que obtiene el CURP del usuario
 * @type {ComputedRef<string>}
 */
const userCurp = computed(() => store.state.global.user.me.curp || '')

/**
 * @description Emits del componente
 * @param {boolean} update:modelValue - Actualiza el valor del modal
 */
const emit = defineEmits<(e: 'update:modelValue', value: boolean) => void>()

/**
 * @description Computed property que controla la visibilidad del modal
 * @type {ComputedRef<boolean>}
 */
const showModal = computed({
	get: () => props.modelValue,
	set: value => emit('update:modelValue', value)
})

/**
 * @description Función que cierra el modal
 */
const closeModal = () => {
	showModal.value = false
}

/**
 * @description Función que formatea una fecha al formato local mexicano
 * @param {string | number | Date} date - Fecha a formatear
 * @returns {string} Fecha formateada
 */
const formatDate = (date: string | number | Date) => {
	if (!date) return 'No disponible'
	return new Date(date).toLocaleString('es-MX', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric'
	})
}

/**
 * @description Computed property que obtiene el certificado del usuario
 * @type {ComputedRef<any>}
 */
const certificado = computed(() => store.state.global.user.me.cer)

/**
 * @description Computed property que obtiene los responsables del paquete
 * @type {ComputedRef<any>}
 */
const responsables = computed(() => props.package?.responsables)

/**
 * @description Referencia que indica si el paquete está firmado
 * @type {Ref<boolean>}
 */
const paqueteFirmado = ref(false)

/**
 * @description Referencia que indica si la firma está completada
 * @type {Ref<boolean>}
 */
const firmaCompletada = ref(false)

/**
 * @description Referencia que almacena la cara de la firma
 * @type {Ref<string>}
 */
const face = ref('')

/**
 * @description Referencia que almacena la firma de la cara
 * @type {Ref<string>}
 */
const signatureFace = ref('')

/**
 * @description Referencia que indica si está cargando
 * @type {Ref<boolean>}
 */
const loading = ref(false)

/**
 * @description Función que obtiene el ZIP del paquete
 * @param {number} id - ID del paquete
 */
const getZip = async (id: number, name: string) => {
	try {
		await certificateService.getZipOfPackage({
			package: id,
			institution: props.currentInstitution,
			testMode: props.isTestMode,
			name: name
		})
	} catch (error: any) {
		showErrorModal.value = true
		errorMessage.value = error.message || 'Error al obtener el ZIP del paquete'
	}
}

/**
 * @description Referencia que almacena los datos de la firma
 * @type {Ref<{ firmas: string[]; face: string; signatureFace: string } | null>}
 */
const firmaData = ref<{
	firmas: string[]
	face: string
	signatureFace: string
} | null>(null)

/**
 * @description Función que maneja la firma completada
 * @param {{ firmas: string[]; face: string; signatureFace: string }} data - Datos de la firma
 */
const handleFirmaCompletada = async (data: { firmas: string[]; face: string; signatureFace: string }) => {
	firmaData.value = data
	firmaCompletada.value = true
}

/**
 * @description Función que maneja los errores de firma
 * @param {string} error - Mensaje de error
 */
const handleFirmaError = (error: string) => {
	console.error('Error al procesar las firmas:', error)
	store.dispatch('notification/showError', error)
}

/**
 * @description Función que solicita la firma de los certificados
 * @async
 * @returns {Promise<void>}
 */
const requestSign = async () => {
	if (props.package === null || !firmaData.value) return
	try {
		loading.value = true
		const packageToSign = props.package

		// Obtener los títulos del paquete
		const degreesRef = dbRef(db, getInstitutionPath('/certificates/queue'))
		const packageQuery = query(degreesRef, orderByChild('package'), equalTo(packageToSign?.id ?? 0))
		const snapshot = await get(packageQuery)

		if (!snapshot.exists()) {
			throw new Error('No se encontraron títulos en el paquete')
		}

		const documents = snapshot.val() as IExtendedCertificate
		const certificatesToSign = []

		// Validar que tengamos firmas para todos los documentos
		if (!firmaData.value.firmas || firmaData.value.firmas.length === 0) {
			throw new Error('No se encontraron firmas generadas')
		}

		// Preparar los títulos para firmar
		for (const [id, certificate] of Object.entries(documents)) {
			let cadenaToSign = ''

			// Obtener la cadena original correcta
			if (typeof certificate.cadenaOriginal === 'object') {
				cadenaToSign = certificate.cadenaOriginal[userCurp.value]
			} else if (typeof certificate.cadenaOriginal === 'string') {
				cadenaToSign = certificate.cadenaOriginal
			}

			if (!cadenaToSign) {
				console.warn(`No se encontró cadena original para el documento ${id}`)
				continue
			}

			// Encontrar la firma correspondiente a esta cadena
			const indexByCadena = props.cadenasAFirmar.findIndex(cadenaAFirmar => cadenaAFirmar === cadenaToSign)

			if (indexByCadena === -1) {
				console.warn(`No se encontró firma correspondiente para el documento ${id}`)
				continue
			}

			const firma = firmaData.value.firmas[indexByCadena]
			if (!firma) {
				console.warn(`La firma para el documento ${id} es inválida`)
				continue
			}

			certificatesToSign.push({
				id,
				signature: firma
			})
		}

		// Validar que tengamos certificados para firmar
		if (certificatesToSign.length === 0) {
			throw new Error('No se encontraron certificados válidos para firmar')
		}

		// Actualizar las variables de firma
		face.value = firmaData.value.face
		signatureFace.value = firmaData.value.signatureFace

		// Llamar al servicio addSignatures
		await certificateService.addSignatures(props.currentInstitution, face.value, signatureFace.value, props.deviceId, certificatesToSign, testMode.value)

		showModal.value = false
		firmaCompletada.value = false
		firmaData.value = null
	} catch (error: any) {
		console.error('Error al procesar las firmas:', error)
		store.dispatch('notification/showError', error.message || 'Error al procesar las firmas')
	} finally {
		loading.value = false
	}
}
</script>
<template>
	<VaModal v-model="showModal" hide-default-actions noDismiss noEscDismiss noOutsideDismiss background-color="transparent">
		<VaCard v-if="props.package" class="ml20">
			<VaCardTitle :class="[props.isTestMode ? 'bg-gradient-paquete-test' : 'bg-gradient-paquete', 'rounded-t-xl']">
				<div class="row align-items-center">
					<div class="row">
						<div class="col-md-11 text-left" style="font-size: 22px; font-weight: bold; color: white">Detalles del certificado</div>
						<div class="col-md-1 text-right ml2">
							<VaButton round icon="cancel" :color="!props.isTestMode ? '#0d6efd' : '#7f1d1d'" type="button" @click="closeModal" style="color: white; font-weight: bold" />
						</div>
					</div>
				</div>
			</VaCardTitle>
			<VaCardContent>
				<div v-if="props.package.sign" class="package-signed-alert mt-2">
					<div class="alert-content">
						<div class="alert-icon">
							<i class="fas fa-check-circle"></i>
						</div>
						<div class="alert-text">
							<h5 class="alert-title">Paquete Firmado</h5>
							<p class="alert-message">Los documentos de este paquete ya han sido firmados y están listos para su descarga.</p>
						</div>
					</div>
				</div>
				<div class="row" v-else>
					<div class="col-md-12 mb-4">
						<div class="signers-section">
							<h4 class="signers-title">
								<i class="fas fa-users me-2"></i>
								Responsables de firma
							</h4>
							<div class="signers-grid">
								<div v-for="(responsable, key) in responsables" :key="key" class="signer-card" :class="{ 'signed': responsable.sign, 'current-user': key === userCurp }">
									<div class="signer-header">
										<div class="signer-avatar">
											<i class="fas fa-user"></i>
										</div>
										<div class="signer-info">
											<h5 class="signer-name">{{ responsable.name }}</h5>
											<p class="signer-role">{{ responsable.cargo }}</p>
										</div>
										<div class="signer-status">
											<VaBadge :text="responsable.sign ? 'Firmado' : 'Pendiente'" :color="responsable.sign ? 'success' : 'warning'" class="status-badge" />
											<div v-if="responsable.sign" class="sign-icon">
												<i class="fas fa-check-circle"></i>
											</div>
										</div>
									</div>
									<div v-if="!responsable.sign && key === userCurp" class="signer-actions">
										<div v-if="responsable.signing" class="signing-indicator">
											<i class="fas fa-spinner fa-spin"></i>
											<span>Firmando...</span>
										</div>
										<FirmaComponent
											style="width: 100%; height: 300px"
											:cadenas-a-firmar="cadenasAFirmar"
											:documentos-firmados="paqueteFirmado"
											:certificado-responsable="certificado.certificadoResponsable"
											@firma-completada="handleFirmaCompletada"
											@firma-error="handleFirmaError" />
										<VaCard class="certificate-card" flat style="margin-top: 15px">
											<VaCardTitle class="card-title">
												<VaIcon name="verified" class="title-icon" />
												Certificado digital del responsable
											</VaCardTitle>
											<VaCardContent>
												<div class="certificate-info">
													<div class="certificate-item">
														<VaIcon name="description" class="certificate-icon" />
														<div class="certificate-content">
															<label class="certificate-label" for="certificate-name">Nombre del certificado</label>
															<span class="certificate-value">{{ certificado.x509.subject.commonName }}</span>
														</div>
													</div>
													<div class="certificate-item">
														<VaIcon name="fingerprint" class="certificate-icon" />
														<div class="certificate-content">
															<label class="certificate-label" for="certificate-serial">Número de serie</label>
															<span class="certificate-value">{{ certificado.x509.serial }}</span>
														</div>
													</div>
													<div class="certificate-item">
														<VaIcon name="event" class="certificate-icon" />
														<div class="certificate-content">
															<label class="certificate-label" for="certificate-notBefore">Fecha de emisión</label>
															<span class="certificate-value">{{ formatDate(certificado.x509.notBefore) }}</span>
														</div>
													</div>
													<div class="certificate-item">
														<VaIcon name="schedule" class="certificate-icon" />
														<div class="certificate-content">
															<label class="certificate-label" for="certificate-notAfter">Fecha de expiración</label>
															<span class="certificate-value">{{ formatDate(certificado.x509.notAfter) }}</span>
														</div>
													</div>
												</div>
											</VaCardContent>
										</VaCard>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="col-md-12 mb-4" v-if="props.package.sign && !props.package.sent">
					<div class="download-section">
						<div class="status-container">
							<div v-if="!props.package.success && !props.package.error" class="status-card status-pending">
								<div class="status-icon">
									<i class="fas fa-spinner fa-spin"></i>
								</div>
								<div class="status-content">
									<h5 class="status-title">Validando documentos</h5>
									<p class="status-message">Esperando validación del servidor...</p>
								</div>
							</div>

							<div v-if="props.package.error" class="status-card status-error">
								<div class="status-icon">
									<i class="fas fa-exclamation-triangle"></i>
								</div>
								<div class="status-content">
									<h5 class="status-title">Error de validación</h5>
									<p class="status-message">{{ props.package.errorMessage }}</p>
								</div>
							</div>

							<div v-if="!props.package.error && props.package.success" class="status-card status-success">
								<div class="status-icon">
									<i class="fas fa-check-circle"></i>
								</div>
								<div class="status-content">
									<h5 class="status-title">Validación exitosa</h5>
									<p class="status-message">Los documentos han sido validados correctamente</p>
								</div>
							</div>
						</div>

						<div v-if="!props.package.error && props.package.success" class="download-container">
							<VaButton class="download-button" @click="getZip(props.package.id, props.package.label)" :disabled="!props.package.success">
								<div class="button-content">
									<i class="fas fa-cloud-download"></i>
									<span>Descargar paquete ZIP</span>
								</div>
							</VaButton>
						</div>
					</div>
				</div>
			</VaCardContent>
			<VaCardActions class="d-flex justify-content-end">
				<VaButton v-if="!props.package.sign" type="button" class="btn btn-primary" @click="requestSign" :disabled="!firmaCompletada">
					<i class="fas fa-paper-plane me-2"></i>
					Enviar firmas
				</VaButton>
			</VaCardActions>
		</VaCard>
	</VaModal>
	<BaseModal v-model="showErrorModal" title="Error" :message="errorMessage" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>
<style scoped>
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
.certificate-info {
	display: flex;
	flex-direction: column;
	gap: 5px;
	padding: 12px 0;
}

.certificate-item {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px;
	background: #f8f9fa;
	border-radius: 12px;
	border-left: 4px solid #28a745;
}

.certificate-icon {
	color: #28a745;
	font-size: 1.25rem;
}

.certificate-content {
	flex: 1;
}

.certificate-label {
	display: block;
	font-size: 0.875rem;
	font-weight: 500;
	color: #6c757d;
	margin-bottom: 0.25rem;
}

.certificate-value {
	display: block;
	font-size: 1rem;
	font-weight: 600;
	color: #1a2a3a;
	word-break: break-all;
}
.certificate-card {
	background: white;
	border-radius: 16px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
	border: 1px solid rgba(0, 0, 0, 0.05);
}

.card-title {
	display: flex;
	align-items: center;
	gap: 5px;
	font-size: 16px;
	font-weight: 600;
	color: #1a2a3a;
	padding: 12px 12px 0 12px;
}

/* Estilos para la sección de firmantes */
.signers-section {
	margin-bottom: 2rem;
}

.signers-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
}

.signers-grid {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.signer-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.signer-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.signer-card.signed {
	border-color: #28a745;
	background: linear-gradient(135deg, #f8fff9 0%, #ffffff 100%);
}

.signer-card.current-user {
	border-color: #007bff;
	background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
	position: relative;
}

.signer-card.current-user::before {
	content: 'Tú';
	position: absolute;
	top: -8px;
	left: 1rem;
	background: #007bff;
	color: white;
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 0.75rem;
	font-weight: 600;
}

.signer-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1rem;
}

.signer-avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 1.25rem;
	flex-shrink: 0;
}

.signer-info {
	flex: 1;
	min-width: 0;
}

.signer-name {
	font-size: 1.1rem;
	font-weight: 600;
	color: #1a2a3a;
	margin: 0 0 0.25rem 0;
	line-height: 1.3;
}

.signer-role {
	font-size: 0.9rem;
	color: #6c757d;
	margin: 0;
	font-weight: 500;
}

.signer-status {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-shrink: 0;
}

.status-badge {
	font-weight: 600;
	font-size: 0.8rem;
	padding: 0.25rem 0.75rem;
}

.sign-icon {
	color: #28a745;
	font-size: 1.25rem;
	animation: fadeIn 0.5s ease-in-out;
}

.signer-actions {
	margin-top: 1rem;
	padding-top: 1rem;
	border-top: 1px solid #e9ecef;
}

.signing-indicator {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	color: #007bff;
	font-weight: 500;
	margin-bottom: 1rem;
	padding: 0.75rem;
	background: #f8fbff;
	border-radius: 8px;
	border-left: 4px solid #007bff;
}

.signing-indicator i {
	font-size: 1rem;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Estilos para la alerta de paquete firmado */
.package-signed-alert {
	background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
	border: 1px solid #c3e6cb;
	border-radius: 16px;
	padding: 1.5rem;
	margin-bottom: 2rem;
	box-shadow: 0 4px 20px rgba(40, 167, 69, 0.15);
}

.alert-content {
	display: flex;
	align-items: center;
	gap: 1rem;
}

.alert-icon {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background: #28a745;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 1.5rem;
	flex-shrink: 0;
}

.alert-text {
	flex: 1;
}

.alert-title {
	font-size: 1.1rem;
	font-weight: 600;
	color: #155724;
	margin: 0 0 0.5rem 0;
}

.alert-message {
	font-size: 0.95rem;
	color: #155724;
	margin: 0;
	opacity: 0.9;
}

/* Estilos para la sección de descarga */
.download-section {
	background: white;
	border-radius: 16px;
	border: 1px solid #e9ecef;
	padding: 2rem;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.status-container {
	margin-bottom: 2rem;
}

.status-card {
	display: flex;
	align-items: center;
	gap: 1rem;
	padding: 1.5rem;
	border-radius: 12px;
	border-left: 4px solid;
	transition: all 0.3s ease;
}

.status-pending {
	background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
	border-left-color: #ffc107;
}

.status-error {
	background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
	border-left-color: #dc3545;
}

.status-success {
	background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
	border-left-color: #28a745;
}

.status-icon {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.25rem;
	flex-shrink: 0;
}

.status-pending .status-icon {
	background: #ffc107;
	color: #856404;
}

.status-error .status-icon {
	background: #dc3545;
	color: white;
}

.status-success .status-icon {
	background: #28a745;
	color: white;
}

.status-content {
	flex: 1;
}

.status-title {
	font-size: 1.1rem;
	font-weight: 600;
	margin: 0 0 0.5rem 0;
}

.status-pending .status-title {
	color: #856404;
}

.status-error .status-title {
	color: #721c24;
}

.status-success .status-title {
	color: #155724;
}

.status-message {
	font-size: 0.95rem;
	margin: 0;
	opacity: 0.9;
}

.status-pending .status-message {
	color: #856404;
}

.status-error .status-message {
	color: #721c24;
}

.status-success .status-message {
	color: #155724;
}

.download-container {
	text-align: center;
}

.download-button {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%) !important;
	border: none !important;
	border-radius: 12px !important;
	padding: 1rem 2rem !important;
	font-size: 1.1rem !important;
	font-weight: 600 !important;
	color: white !important;
	transition: all 0.3s ease !important;
	box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3) !important;
	min-width: 250px !important;
}

.download-button:hover {
	transform: translateY(-2px) !important;
	box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4) !important;
}

.download-button:disabled {
	background: linear-gradient(135deg, #6c757d 0%, #545b62 100%) !important;
	transform: none !important;
	box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3) !important;
	cursor: not-allowed !important;
}

.button-content {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.75rem;
}

.button-content i {
	font-size: 1.25rem;
}

/* Responsive design */
@media (max-width: 768px) {
	.signer-header {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.75rem;
	}

	.signer-status {
		align-self: flex-end;
	}

	.signer-card {
		padding: 1rem;
	}

	.alert-content {
		flex-direction: column;
		text-align: center;
		gap: 0.75rem;
	}

	.status-card {
		flex-direction: column;
		text-align: center;
		gap: 0.75rem;
	}

	.download-section {
		padding: 1.5rem;
	}

	.download-button {
		min-width: 200px !important;
		padding: 0.875rem 1.5rem !important;
	}
}
/* Gradientes para el header */
.bg-gradient-paquete {
	background: linear-gradient(90deg, #2563eb 0%, #1e3a8a 100%);
}
.bg-gradient-paquete-test {
	background: linear-gradient(90deg, #dc2626 0%, #7f1d1d 100%);
}
</style>
